import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import prefetchRouterInitialize from "@tata1mg/prefetch-core/react"
import {
    setIsBot,
    setVisitorId,
    setLoggedIn,
    setCorporateDetails,
    updateUserData,
    updateAuthConfig,
} from "./reducer"
import { updateCurrentCity, updateCurrentCityData, updateCities } from "@containers/LocationPicker/reducer"
import synapse from "@tata1mg/synapse"
import Footer from "@containers/Footer"
import Header from "@containers/Header"
import SnackBar from "@components/SnackBar/SnackBar"
import { useLocation, useNavigationType, UNSAFE_LocationContext, Outlet } from "react-router-dom"
import { HelmetProvider } from "react-helmet-async"
import SEO from "@components/SEO/SEO"
import Auth from "@flows/Auth/Auth"
import { isAdmin, isTelesales } from "@lib/utils"
import { setCartType } from "@containers/App/reducer"
import { getCartType, safeLogger } from "@commonUtils"
import { MD5 } from "@commonUtils/md5"

// Initialize the prefetch resourceLoader
prefetchRouterInitialize({
    manifestFilePath: `${process.env.PUBLIC_ASSET_PATH}route-manifest.json`,
})

/* eslint-disable-next-line */
const App = ({ code }) => {
    const errorCodes = [404, 500]
    const location = useLocation()
    const dispatch = useDispatch()
    const navigationType = useNavigationType()
    const { snackBarMessage, showFooter, showHeader } = useSelector((state) => state.shellReducer)

    useEffect(() => {
        dispatch(setCartType(getCartType(location.pathname)))
    }, [location.pathname])

    const shouldAddKey = () => {
        return !location.pathname.includes("/auth")
    }

    synapse.initialize({
        nativeBridge: {
            bridgeName: "AppCallbacks",
            androidBridgePath: "window.AppCallbacks",
            iosBridgePath: "window.webkit.messageHandlers",
        },
        webBridge: {
            bridgeName: "WebCallbacks",
        },
    })
    /**
     * UNSAFE_LocationContext is being used as location context is required by AnimatePresence for to work properly.
     * By accessing the location context, AnimatePresence can know which component is being removed
     * and which component is being added, allowing it to apply the appropriate enter and exit animations.
     */
    const page =
        code && errorCodes.indexOf(code) != -1 ? (
            <div>Oops, something went wrong !!!</div>
        ) : (
            // This key is required for page transitions with AnimatePresence. Will be removed after we will implement browser's page transition api
            <div data-abc={shouldAddKey() ? location.key : ""} key={shouldAddKey() ? location.key : ""}>
                <UNSAFE_LocationContext.Provider value={{ location, navigationType }}>
                    <Outlet />
                </UNSAFE_LocationContext.Provider>
            </div>
        )
    return (
        <>
            <HelmetProvider>
                <SEO />
                {showHeader && <Header />}
                {page}
                <Auth />
                {snackBarMessage && <SnackBar message={snackBarMessage} />}
                {showFooter && <Footer />}
            </HelmetProvider>
        </>
    )
}

App.serverSideFunction = ({ store, req }, cityServiceableData, citiesData) => {
    store.dispatch(setCartType(getCartType(req.baseUrl)))

    const visitorId = req.cookies["VISITOR-ID"] || req.headers?.["visitor-id"] || req.headers?.["VISITOR-ID"]
    store.dispatch(setVisitorId(visitorId))

    const isBot = req?.deviceDetails?.googleBot ? true : false
    if (isBot) {
        store.dispatch(setIsBot())
    }

    // set user data using req.session
    if (req.session) {
        // remove AUTH_TOKEN, TEMP_USER_ID and userRoles keys and store in Shell Reducer
        const userData = { ...req.session }
        if (userData.AUTH_TOKEN) {
            delete userData.AUTH_TOKEN
        }
        if (userData.TEMP_USER_ID) {
            delete userData.TEMP_USER_ID
        }
        if (userData.userRoles) {
            if (isAdmin(userData.userRoles) || isTelesales(userData.userRoles)) {
                userData.showSuperLogin = true
                userData.showSuperLogout = false
            }
            delete userData.userRoles
        }
        if (userData.emailId) {
            userData["hashedEmail"] = MD5(userData.emailId)
        }

        if (req?.cookies["superLoginToken"]) {
            userData.showSuperLogin = false
            userData.showSuperLogout = true
        }

        store.dispatch(updateUserData(userData))
    }

    if ((req && req.cookies && req.cookies["loggedIn"]) || req.session.AUTH_TOKEN) {
        store.dispatch(setLoggedIn(true))
        let corporateDetails = req?.cookies?.corporateDetails
        // Set corporate details in shell reducer for corporate user
        if (corporateDetails) {
            try {
                corporateDetails = JSON.parse(corporateDetails)
                store.dispatch(setCorporateDetails(corporateDetails))
            } catch (e) {
                safeLogger.error({
                    message: `Error while parsing corporate details cookie - ${e.toString()}`,
                    traceback: e,
                })
            }
        }
    }
    const { city = "New Delhi" } = req.cookies
    store.dispatch(updateCurrentCity(city))

    if (cityServiceableData) {
        store.dispatch(updateCurrentCityData(cityServiceableData))
    }

    if (citiesData) {
        store.dispatch(updateCities(citiesData))
    }
    if (req.headers?.["merchant_phone_number"]) {
        store.dispatch(
            updateAuthConfig({
                populateData: {
                    phone: req.headers["merchant_phone_number"],
                },
            })
        )
    }

    if (req.headers?.["login_conflict"]?.conflict) {
        store.dispatch(
            updateAuthConfig({
                populateData: {
                    requiredDetail:
                        req.headers["login_conflict"]?.conflict_data.length > 0 ? "email" : "phone",
                    conflict: req.headers["login_conflict"]?.conflict,
                    description: req.headers["login_conflict"]?.description,
                    header: req.headers["login_conflict"]?.header,
                    cta: req.headers["login_conflict"]?.cta,
                    optionsList: req.headers["login_conflict"]?.conflict_data,
                    number: req.headers["login_conflict"]?.number,
                },
            })
        )
    }
    return Promise.resolve()
}

export default App
